// Cliente HTTP minimo del login portal.
//
// Decision: duplicamos los tipos publicos del backend (BrandingConfig, etc.)
// en vez de importar desde `libs/common`. El SPA se bundlea con Vite y no
// comparte tsconfig con el backend (Lambdas con NodeNext, SPA con bundler).
// Acoplar ambos por imports directos complicaria el build sin beneficio real
// — el contrato vive en `docs/api/` (OpenAPI) y si divergen, los tests de
// integracion los detectan.
//
// Este tipo debe mantenerse alineado con BrandingConfig de
// `libs/common/types.ts`. Si cambia uno, actualizar el otro.

import { getBrowserId, saveBrowserTicket } from './browser-identity';
import type { AccountMismatch } from './account-guard';

export type { AccountMismatch };

export interface BrandingPalette {
  primary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  text_muted: string;
  error: string;
  // Opcional: branding guardado antes de 2026-07 no lo tiene. Sin valor, el
  // ThemeProvider deja actuar el verde default de index.css (por tema).
  success?: string;
}

export interface BrandingConfig {
  display_name: string;
  logo_url: string;
  logo_dark_url?: string;
  favicon_url?: string;
  theme_mode: 'light' | 'dark' | 'auto';
  colors: BrandingPalette;
  colors_dark?: BrandingPalette;
  font_family: string;
  font_url?: string;
  tos_url?: string;
  privacy_url?: string;
  footer_html?: string;
  contest_active?: boolean;
  contest_name?: string;
  contest_terms_url?: string;
}

// Backend response shape (libs/common/response.ts):
//  - 2xx: body es directamente el payload (NO hay envelope { ok, data, ... }).
//  - 4xx/5xx: body es { error, message, request_id } (AppError) o
//    { error, error_description, error_uri? } (OAuthError).
interface ErrorBody {
  error?: string;
  message?: string;
  error_description?: string;
  request_id?: string;
}

class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export { ApiError };

/**
 * Base URL del API.
 *
 * Resolucion en orden:
 *   1. `import.meta.env.VITE_API_BASE` (build-time env, ej:
 *      `VITE_API_BASE=https://api.auth.lpfplay-dev.com`).
 *   2. Default `''` → relative URLs. En dev local, Vite proxyea `/api/*` a
 *      `http://localhost:3001` (ver vite.config.ts).
 *
 * Cuando se setea via env, NO se aplica el prefijo `/api`. El SPA llama
 * al API directamente (ej: `https://api.auth.lpfplay-dev.com/auth/login`).
 *
 * Para el dev local mantenemos el prefijo `/api` que Vite stripea via proxy.
 */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    // fetch rechaza ante un fallo de red real (offline, DNS, CORS, server caido)
    // con un TypeError opaco. Lo normalizamos a `network_error` para que los
    // callers lo distingan de un error del servidor y muestren el mensaje justo.
    throw new ApiError('network_error', `network request to ${path} failed`, 0);
  }

  // Cuerpo opcional para 204/no-content. El resto siempre devuelve JSON.
  let body: unknown = null;
  if (res.status !== 204) {
    try {
      body = await res.json();
    } catch {
      throw new ApiError('network_error', `invalid JSON from ${path}`, res.status);
    }
  }

  if (!res.ok) {
    const err = (body ?? {}) as ErrorBody;
    const code = err.error ?? 'unknown_error';
    const message = err.message ?? err.error_description ?? res.statusText;
    throw new ApiError(code, message, res.status, err.request_id);
  }

  return body as T;
}

/**
 * GET /partners/{partnerId}/branding.
 *
 * Devuelve null si el partner no existe (404) — el SPA debe caer al default
 * branding y mostrar "invalid tenant" al usuario. Otros errores (5xx,
 * network) propagan como ApiError para que el caller decida.
 */
export async function fetchBranding(partnerId: string): Promise<BrandingConfig | null> {
  try {
    return await fetchJson<BrandingConfig>(`/partners/${encodeURIComponent(partnerId)}/branding`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Auth flow — los endpoints siguen distintos shapes:
//   - /auth/login              → envelope { ok, data, ... }
//   - /dbconnections/*         → envelope tambien (lo implementamos nosotros)
//   - /oauth/device/verify     → envelope
// Por eso todos van por fetchJson.
// ---------------------------------------------------------------------------

export interface LoginParams {
  email: string;
  password: string;
  partner_id: string;
  client_id: string;
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: 'S256';
  scope: string;
  state: string;
  nonce?: string;
}

// Re-consentimiento de T&C (ADR 0008). /auth/login y /oauth/device/verify
// pueden responder esto en vez de completar la accion si el usuario no acepto
// la version vigente. El SPA muestra el interstitial y resuelve con consentAccept.
export interface TosNotice {
  version: string;
  tos_url: string | null;
  status: 'grace' | 'expired';
  grace_deadline: string | null;
}

export interface ConsentRequired {
  consent_required: true;
  tos: TosNotice;
  consent_ticket: string;
  /** Solo en la respuesta de /auth/login. Ver lib/browser-identity.ts. */
  browser_ticket?: string;
}

/** Type guard: ¿la respuesta es un pedido de re-consentimiento? */
export function isConsentRequired(r: unknown): r is ConsentRequired {
  return (
    typeof r === 'object' &&
    r !== null &&
    (r as { consent_required?: unknown }).consent_required === true
  );
}

export type LoginResult =
  | {
      redirect_to: string;
      /** Constancia de con qué cuenta inició sesión este navegador. No es una credencial. */
      browser_ticket?: string;
    }
  | ConsentRequired;

/**
 * POST /auth/login. Autentica al usuario y genera authorization_code. La
 * respuesta incluye `redirect_to`: el SPA debe hacer `window.location.href =
 * redirect_to` para devolver al usuario a la app del partner con el code.
 *
 * Si el usuario debe re-aceptar T&C (ADR 0008), devuelve ConsentRequired en
 * lugar de redirect_to — usar isConsentRequired() para discriminar.
 */
export async function login(params: LoginParams): Promise<LoginResult> {
  const result = await fetchJson<LoginResult>('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  // La constancia se guarda ACÁ y no en cada pantalla a propósito: este es el
  // único lugar por el que pasa toda respuesta de /auth/login (Login, el login
  // manual de Subscribe / Pay / ChangePassword vía loginForAccessToken, y el
  // re-chequeo del alta), así que un llamador nuevo no puede olvidárselo. Los
  // otros caminos que inician sesión (signup, confirmEmailVerification y
  // consentAccept) siguen el mismo criterio en su propia función. Vale
  // también cuando la respuesta es `consent_required`: las credenciales ya se
  // validaron. Una respuesta sin ticket no borra el que hubiera.
  saveBrowserTicket(params.partner_id, result.browser_ticket);
  return result;
}

// ---------------------------------------------------------------------------
// Sesión de pago (self-OAuth) — el portal obtiene su propio access_token para
// poder llamar a /me/* y /subscriptions/* (Bearer). Hace el flujo Auth Code +
// PKCE contra nosotros mismos: /auth/login → code → /oauth/token → access_token.
// Sin redirect del browser: el code se extrae del `redirect_to` del login.
// ---------------------------------------------------------------------------

function base64urlBytes(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generatePkce(): Promise<{ verifier: string; challenge: string }> {
  const verifier = base64urlBytes(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: base64urlBytes(new Uint8Array(digest)) };
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

async function exchangeCodeForToken(
  partnerId: string,
  code: string,
  redirectUri: string,
  codeVerifier: string,
  clientId: string,
): Promise<TokenResponse> {
  const res = await fetch(`${API_BASE}/partners/${encodeURIComponent(partnerId)}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirectUri,
      client_id: clientId,
      code_verifier: codeVerifier,
    }).toString(),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, string>;
  if (!res.ok) {
    throw new ApiError(
      body['error'] ?? 'token_error',
      body['error_description'] ?? body['message'] ?? 'token exchange failed',
      res.status,
    );
  }
  return body as unknown as TokenResponse;
}

/**
 * Autentica con email+password y devuelve un access_token opaco usable como
 * Bearer. Hace el dance Auth Code + PKCE internamente. Tira ApiError
 * `consent_required` si el usuario debe re-aceptar T&C.
 */
export async function loginForAccessToken(opts: {
  partnerId: string;
  email: string;
  password: string;
  clientId: string;
  redirectUri: string;
}): Promise<string> {
  const { verifier, challenge } = await generatePkce();
  const state = base64urlBytes(crypto.getRandomValues(new Uint8Array(16)));
  const result = await login({
    email: opts.email,
    password: opts.password,
    partner_id: opts.partnerId,
    client_id: opts.clientId,
    redirect_uri: opts.redirectUri,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    scope: 'openid profile',
    state,
  });
  if (isConsentRequired(result)) {
    throw new ApiError(
      'consent_required',
      'Tenés que aceptar los nuevos Términos y Condiciones antes de continuar.',
      403,
    );
  }
  const code = new URL(result.redirect_to).searchParams.get('code');
  if (!code) throw new ApiError('login_failed', 'no authorization code returned', 500);
  const tokens = await exchangeCodeForToken(opts.partnerId, code, opts.redirectUri, verifier, opts.clientId);
  return tokens.access_token;
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

// ---------------------------------------------------------------------------
// /subscribe — gestión de plan (§6.2 contrato OTT): adquirir / upgrade /
// downgrade / cancelar. La OTT manda al usuario con id_token_hint + return_url.
// ---------------------------------------------------------------------------

export interface SubscribeInitResult {
  // Presente solo cuando la identidad vino por id_token_hint (mintea sesión
  // corta). En el modo Bearer el SPA ya tiene su propio token.
  access_token?: string;
  user_id: string;
  return_url_ok: boolean;
  /** Email completo de la cuenta sobre la que se opera. */
  account_email?: string | null;
  /** Email enmascarado (`ju***@gmail.com`). Respaldo de backends viejos. */
  account_hint?: string | null;
  /**
   * true = identidad corroborada (login con contraseña, o este navegador ya
   * había iniciado sesión como esa cuenta); false = solo la afirma el enlace.
   */
  account_confirmed?: boolean;
  current: {
    status: MeSubscription['status'];
    plan: {
      plan_id: string;
      code: string;
      display_name: string;
      price_amount: number;
      price_currency: string;
    };
    current_period_end?: string | null;
    /** No-null con la sub viva = se dio de baja: conserva el plan hasta current_period_end. */
    cancelled_at?: string | null;
  } | null;
}

/**
 * Inicia /subscribe: valida client + return_url (whitelist server-side) y
 * devuelve el plan actual. Identidad: `idTokenHint` (mintea sesión corta, sin
 * re-login) o `bearer` (el SPA ya logueó al usuario y solo valida/lee). Reusa
 * el endpoint de checkout en modo `subscribe_init`.
 */
export async function subscribeInit(params: {
  clientId: string;
  returnUrl: string;
  idTokenHint?: string;
  bearer?: string;
  /** Constancia del navegador (lib/browser-identity.ts). Solo viaja junto al hint. */
  browserTicket?: string | null;
}): Promise<SubscribeInitResult | AccountMismatch> {
  return fetchJson<SubscribeInitResult | AccountMismatch>('/subscriptions/checkout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(params.bearer ? authHeaders(params.bearer) : {}),
    },
    body: JSON.stringify({
      subscribe_init: true,
      client_id: params.clientId,
      ...(params.idTokenHint ? { id_token_hint: params.idTokenHint } : {}),
      // Solo acompaña al enlace: con Bearer la identidad ya es la de la sesión.
      ...(params.idTokenHint && params.browserTicket
        ? { browser_ticket: params.browserTicket }
        : {}),
      ...(params.idTokenHint ? { browser_id: getBrowserId() } : {}),
      return_url: params.returnUrl,
    }),
  });
}

/** POST /subscriptions/checkout { change_to_free } (Bearer) → downgrade sin cobro. */
export async function changePlanToFree(
  token: string,
  freePlanId: string,
): Promise<{
  status: string;
  changed: boolean;
  /** 'period_end': conserva el plan pago hasta access_until y despues pasa al gratuito. */
  effective?: 'period_end' | 'now';
  access_until?: string | null;
}> {
  return fetchJson('/subscriptions/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ change_to_free: true, plan_id: freePlanId }),
  });
}

/** POST /subscriptions/cancel (Bearer) → cancela la suscripción viva. */
export async function cancelSubscription(
  token: string,
  reason?: string,
): Promise<{ cancelled: boolean; access_until?: string | null }> {
  return fetchJson('/subscriptions/cancel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

export interface MeSubscription {
  subscription_id: string;
  status: 'pending' | 'trialing' | 'active' | 'grace_period' | 'expired' | 'cancelled';
  plan: {
    plan_id: string;
    code: string;
    display_name: string;
    price_amount: number;
    price_currency: string;
    billing_period: 'monthly' | 'yearly' | 'weekly';
    features: Array<string | { label: string; included?: boolean }>;
  };
  current_period_end: string | null;
  cancelled_at?: string | null;
  /** Pasarela de la sub ('line' | 'mercadopago' | …). */
  payment_provider?: string | null;
}

/** GET /me/subscription (Bearer). null si el usuario no tiene suscripción (404). */
export async function fetchMeSubscription(token: string): Promise<MeSubscription | null> {
  try {
    return await fetchJson<MeSubscription>('/me/subscription', { headers: authHeaders(token) });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** Checkout abierto: hay un pago por hacer (widget o redirect). */
export interface CheckoutSessionOpen {
  subscription_id: string;
  /** 'line' → widget embebido; 'mercadopago' → redirect a su checkout. */
  provider?: string;
  checkout_url: string;
  payment_id: string;
  status: string;
}

/**
 * El backend encontró que un pago ANTERIOR del usuario ya estaba aprobado y
 * activó la suscripción en vez de abrir otro checkout. No trae `checkout_url`:
 * no hay nada que pagar, se trata igual que un confirm exitoso.
 */
export interface CheckoutSessionActivated {
  subscription_id: string;
  provider?: string;
  status: 'active';
  activated: true;
  redirect_to?: string;
  // Ver CouponRedeemResult.redirect_uri_ok.
  redirect_uri_ok?: boolean;
}

/**
 * Hay un pago ANTERIOR de la misma suscripción que la pasarela todavía no
 * resolvió y que todavía puede cobrarse. El backend NO creó otro pago: no hay
 * `checkout_url`, solo el pago en curso para verificar/adoptar. Ver
 * `checkoutOutcome` (lib/checkout-outcome.ts).
 */
export interface CheckoutSessionProcessing {
  subscription_id: string;
  provider?: string;
  status: 'processing';
  activated: false;
  /** El pago anterior, para sondearlo con `confirmCheckoutPayment`. */
  payment_id: string;
  /** Segundos que le quedan a la sesión del widget anterior. */
  retry_after_seconds?: number;
}

/**
 * Respuesta de la creación del checkout. NO leer `checkout_url` directo: pasar
 * por `checkoutOutcome` (lib/checkout-outcome.ts), que distingue los casos.
 */
export type CheckoutSession =
  | CheckoutSessionOpen
  | CheckoutSessionActivated
  | CheckoutSessionProcessing;

/**
 * Credencial del checkout. Dos modos:
 *  - `bearer`: access_token de sesión (flujo con login).
 *  - `payToken`: token acotado del link del email (`/pay?pt=…`) — paga SIN login.
 * El backend deriva userId/partnerId/plan del pay_token; nunca del body.
 */
export type CheckoutAuth = { bearer: string } | { payToken: string };

function checkoutAuth(auth: CheckoutAuth): {
  headers: Record<string, string>;
  body: Record<string, unknown>;
} {
  return 'bearer' in auth
    ? { headers: authHeaders(auth.bearer), body: {} }
    : { headers: {}, body: { pay_token: auth.payToken } };
}

export interface PayContext {
  status: MeSubscription['status'];
  payment_provider?: string | null;
  holder_name: string | null;
  holder_email: string | null;
  /** true = este navegador inició sesión como el titular del link. */
  account_confirmed?: boolean;
  plan: {
    plan_id: string;
    display_name: string;
    price_amount: number;
    price_currency: string;
    billing_period: MeSubscription['plan']['billing_period'];
  };
}

/**
 * POST /subscriptions/checkout { pay_token, context_only:true } → datos para la
 * pantalla anti-estafa (de quién es la cuenta + qué plan se paga). Además marca
 * el email del titular como verificado (redimir el link prueba que existe).
 */
export async function fetchPayContext(
  payToken: string,
  browserTicket?: string | null,
): Promise<PayContext> {
  return fetchJson<PayContext>('/subscriptions/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      pay_token: payToken,
      context_only: true,
      ...(browserTicket ? { browser_ticket: browserTicket } : {}),
    }),
  });
}

/**
 * POST /subscriptions/checkout → crea el pago en Line y devuelve la URL del
 * widget, o avisa que un pago anterior ya estaba aprobado (ver CheckoutSession).
 * Los `authz` params van también acá: si la respuesta es una activación, el
 * backend los necesita para mintear el `redirect_to` de vuelta a la app.
 */
export async function createCheckoutSession(
  auth: CheckoutAuth,
  planId: string,
  customer: { document?: string; name?: string },
  opts: { successUrl?: string; authz?: VerifyAuthzParams | undefined } = {},
): Promise<CheckoutSession> {
  const a = checkoutAuth(auth);
  return fetchJson<CheckoutSession>('/subscriptions/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...a.headers },
    body: JSON.stringify({
      ...a.body,
      plan_id: planId,
      ...(customer.document ? { customer_document: customer.document } : {}),
      ...(customer.name ? { customer_name: customer.name } : {}),
      // Pasarelas con redirect (MP): a donde vuelve el usuario al terminar.
      ...(opts.successUrl ? { success_url: opts.successUrl } : {}),
      ...(opts.authz ?? {}),
    }),
  });
}

export interface CouponRedeemResult {
  subscription_id: string;
  status: string;
  activated: boolean;
  free_days: number;
  current_period_end: string;
  redirect_to?: string;
  // false = mandamos authz params pero el redirect_uri no está registrado en el
  // client → no hay vuelta automática a la app. Ausente si no se mandaron.
  redirect_uri_ok?: boolean;
}

/**
 * POST /subscriptions/checkout con coupon_code → canjea un cupón de días
 * gratis. Reemplaza el pago por completo: si el cupón es válido y del plan
 * elegido, la sub queda activa por free_days sin pasar por el widget de Line.
 * Errores esperables: coupon_not_found, coupon_already_redeemed,
 * coupon_revoked, coupon_plan_mismatch, rate_limited.
 */
export async function redeemCoupon(
  auth: CheckoutAuth,
  planId: string,
  couponCode: string,
  authz?: VerifyAuthzParams,
): Promise<CouponRedeemResult> {
  const a = checkoutAuth(auth);
  return fetchJson('/subscriptions/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...a.headers },
    body: JSON.stringify({
      ...a.body,
      plan_id: planId,
      coupon_code: couponCode,
      ...(authz ?? {}),
    }),
  });
}

export interface ConfirmCheckoutResult {
  status: string;
  activated: boolean;
  /**
   * 'rejected' = la pasarela rechazó ESTE pago: cortar el sondeo. Puede venir
   * con `status: 'active'` (cambio de plan: la sub sigue viva con el plan
   * anterior), por eso se mira antes que `status`. Ver confirmVerdict.
   */
  payment_status?: string;
  subscription_id?: string;
  redirect_to?: string;
  // Ver CouponRedeemResult.redirect_uri_ok.
  redirect_uri_ok?: boolean;
}

/**
 * POST /subscriptions/checkout con payment_id → confirma el pago y activa la sub.
 * Si se pasan los `authz` params (flow OAuth, mismo dispositivo), al activar el
 * backend mintea el code y devuelve `redirect_to` → volver a la OTT logueado.
 */
export async function confirmCheckoutPayment(
  auth: CheckoutAuth,
  planId: string,
  paymentId: string,
  authz?: VerifyAuthzParams,
): Promise<ConfirmCheckoutResult> {
  const a = checkoutAuth(auth);
  return fetchJson('/subscriptions/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...a.headers },
    body: JSON.stringify({ ...a.body, plan_id: planId, payment_id: paymentId, ...(authz ?? {}) }),
  });
}

/**
 * POST /auth/consent-accept. Cierra el interstitial de re-consentimiento.
 * `action: 'accept'` registra la aceptacion; `'skip'` (solo en gracia) sigue
 * sin registrar. Devuelve la accion original completada: redirect_to (login)
 * o approved (device).
 */
export async function consentAccept(
  partnerId: string,
  params: { consentTicket: string; action: 'accept' | 'skip' },
): Promise<{ redirect_to: string; browser_ticket?: string } | { approved: true }> {
  const result = await fetchJson<{ redirect_to: string; browser_ticket?: string } | { approved: true }>(
    '/auth/consent-accept',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        partner_id: partnerId,
        consent_ticket: params.consentTicket,
        action: params.action,
      }),
    },
  );
  // Cierra un login: misma constancia que login() (ver lib/browser-identity.ts).
  if ('browser_ticket' in result) saveBrowserTicket(partnerId, result.browser_ticket);
  return result;
}

export interface SignupParams {
  // Opcional: el backend lo acepta por compat Auth0 pero no lo usa. Solo lo
  // mandamos si venimos del flow OAuth (authz params en la URL).
  client_id?: string;
  email: string;
  password: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  terms_accepted?: boolean;
  tos_version?: string;
  marketing_opt_in?: boolean;
  connection?: string;
  user_metadata?: Record<string, string>;
  /**
   * Selecciones de favoritos exigidas por la config del partner. Cada tipo
   * requerido debe aparecer una vez con `favorite_id` valido o `null` para
   * la opcion "Otro". Si el partner no exige favoritos, omitir o mandar
   * array vacio.
   */
  favorites?: Array<{ favorite_type_id: string; favorite_id: string | null }>;
  // Plan elegido en el wizard de signup. Free (price=0) crea la sub en el alta;
  // un plan pago no crea sub (la respuesta trae `requires_payment:true`).
  plan_id?: string;
  // Authz params del flow OAuth (opcionales). Si se pasan, el backend los
  // arrastra al link de verificacion para habilitar auto-login al confirmar.
  redirect_uri?: string;
  state?: string;
  code_challenge?: string;
  code_challenge_method?: 'S256';
  scope?: string;
  nonce?: string;
  // Nonce de la cookie `av` que el SPA setea al registrar (correlador de
  // navegador para el verify).
  verify_nonce?: string;
}

/**
 * POST /partners/{partnerId}/dbconnections/signup. Auth0-compatible. Crea
 * el user en Cognito + Aurora. Devuelve datos minimos del user creado.
 */
export async function signup(
  partnerId: string,
  params: SignupParams,
): Promise<{
  _id: string;
  email: string;
  email_verified: boolean;
  plan_id: string | null;
  requires_payment: boolean;
  // Plan pago: token para pagar en el mismo dispositivo sin login (/pay?pt=…).
  pay_token?: string;
  // Free + OAuth: el backend ya minteó el code → volver a la OTT logueado.
  redirect_to?: string;
  /** Solo junto con `redirect_to`. Ver lib/browser-identity.ts. */
  browser_ticket?: string;
}> {
  const result = await fetchJson<Awaited<ReturnType<typeof signup>>>(
    `/partners/${encodeURIComponent(partnerId)}/dbconnections/signup`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
  );
  // El alta que termina en sesión deja la misma constancia que login(). Se
  // guarda acá y no en signup-machine para que ningún llamador la olvide.
  saveBrowserTicket(partnerId, result.browser_ticket);
  return result;
}

export interface EmailCheckResult {
  /** Ya existe una cuenta con este email. */
  exists: boolean;
  /**
   * El dominio puede recibir correos (MX/A verificado server-side, cacheado).
   * `true` también ante DNS inconcluso (fail-open); backends viejos que no
   * mandan el campo cuentan como entregable.
   */
  domainDeliverable: boolean;
}

/**
 * Chequeo temprano del email del form de registro: existencia de cuenta +
 * deliverability del dominio. Reusa el endpoint de signup en modo `check_only`
 * (no crea nada). El caller debe dispararlo con criterio (ej: en blur, cuando
 * ambos emails coinciden) para no cargar el backend.
 */
export async function checkEmail(partnerId: string, email: string): Promise<EmailCheckResult> {
  const res = await fetchJson<{ email: string; exists: boolean; domain_deliverable?: boolean }>(
    `/partners/${encodeURIComponent(partnerId)}/dbconnections/signup`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, check_only: true }),
    },
  );
  return { exists: res.exists, domainDeliverable: res.domain_deliverable !== false };
}

// ---------------------------------------------------------------------------
// Plans — catalogo publico de planes del partner (para el plan picker)
// ---------------------------------------------------------------------------

export interface Plan {
  plan_id: string;
  code: string;
  display_name: string;
  price_amount: number;
  price_currency: string;
  billing_period: 'monthly' | 'yearly' | 'weekly';
  max_concurrent_streams: number;
  max_devices: number;
  trial_days: number;
  features: Array<string | { label: string; included?: boolean }>;
  /**
   * Plan destacado del partner (se configura en el backoffice). Lo marca la
   * chapita "Recomendado" y es el primer candidato a venir preseleccionado en
   * la pantalla de cambio de plan. Opcional: un backend viejo no lo manda.
   */
  recommended?: boolean;
}

/**
 * GET /partners/{partnerId}/plans. Planes ACTIVOS del partner para el picker
 * del signup. Ordenados por precio asc (free primero). 404 si el partner no
 * existe.
 */
export async function fetchPlans(partnerId: string): Promise<Plan[]> {
  const res = await fetchJson<{ partner_id: string; plans: Plan[] }>(
    `/partners/${encodeURIComponent(partnerId)}/plans`,
  );
  return [...res.plans].sort((a, b) => a.price_amount - b.price_amount);
}

// ---------------------------------------------------------------------------
// Signup config — tipos de favorito requeridos al alta (M11.x feature)
// ---------------------------------------------------------------------------

export interface SignupRequiredFavoriteConfig {
  favorite_type_id: string;
  label: string;
  title_field: string;
  image_field: string | null;
  initial_limit: number;
}

export interface SignupConfig {
  required_favorites: SignupRequiredFavoriteConfig[];
  // Capacidad por-partner (features.plans_enabled, default false): si es false
  // el wizard saltea el paso de plan y el alta se hace sin plan_id. Permite
  // correr el mismo bundle en envs donde el circuito comercial no esta activo.
  plans_enabled: boolean;
}

/**
 * GET /partners/{partnerId}/signup-config. Devuelve los tipos de favorito que
 * el partner exige seleccionar en el alta, con el mapping (label/title/image)
 * para renderizar el select buscable. Si el partner no exige nada, devuelve
 * `{ required_favorites: [] }`. 404 si el partner no existe.
 */
export async function fetchSignupConfig(
  partnerId: string,
  clientId?: string,
): Promise<SignupConfig | null> {
  // client_id opcional: plans_enabled (y demas config) puede estar overrideado
  // por-client. Sin el, el backend responde con el valor del partner.
  const qs = clientId ? `?client_id=${encodeURIComponent(clientId)}` : '';
  try {
    return await fetchJson<SignupConfig>(
      `/partners/${encodeURIComponent(partnerId)}/signup-config${qs}`,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

// ---------------------------------------------------------------------------
// ToS config — version vigente de Terminos y Condiciones (ADR 0008)
// ---------------------------------------------------------------------------

export interface TosConfig {
  version: string | null;
  tos_url: string | null;
  effective_at: string | null;
  grace_days: number | null;
}

/**
 * GET /partners/{partnerId}/tos. Version VIGENTE de T&C del partner, para
 * mostrar el documento correcto en signup / re-consentimiento. Si el partner
 * todavia no publico ninguna version, los campos vienen null (el caller cae al
 * tos_url del branding). La aceptacion la registra el servidor con la version
 * vigente — el cliente no la asierta.
 */
export async function fetchTosConfig(partnerId: string): Promise<TosConfig | null> {
  try {
    return await fetchJson<TosConfig>(`/partners/${encodeURIComponent(partnerId)}/tos`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export interface PublicFavoriteItem {
  favorite_id: string;
  favorite_type_id: string;
  code: string;
  name: string;
  description: string | null;
  details: Record<string, unknown>;
}

/**
 * GET /partners/{partnerId}/favorite-items?favorite_type_id=&q=&limit=. Devuelve
 * items publicos del catalogo del partner. Sin auth. Usado por el form de
 * signup para popular cada dropdown buscable.
 *
 * Sin `q`: top-N items alfabeticos. Con `q`: full-text + trigram search.
 */
export async function fetchFavoriteItems(
  partnerId: string,
  params: { favorite_type_id: string; q?: string; limit?: number },
): Promise<{ items: PublicFavoriteItem[]; count: number }> {
  const qs = new URLSearchParams();
  qs.set('favorite_type_id', params.favorite_type_id);
  if (params.q !== undefined && params.q !== '') qs.set('q', params.q);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  return fetchJson<{ items: PublicFavoriteItem[]; count: number }>(
    `/partners/${encodeURIComponent(partnerId)}/favorite-items?${qs.toString()}`,
  );
}

/**
 * POST /partners/{partnerId}/dbconnections/change_password. Auth0-compatible.
 * Envia email con reset link. Siempre devuelve 200 (anti-enumeracion de
 * usuarios).
 */
export async function requestPasswordReset(
  partnerId: string,
  email: string,
  clientId: string,
  connection: string = 'Username-Password-Authentication',
): Promise<{ email: string }> {
  return fetchJson(
    `/partners/${encodeURIComponent(partnerId)}/dbconnections/change_password`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, connection, client_id: clientId }),
    },
  );
}

/**
 * Authz params del flow OAuth, arrastrados desde el signup hasta el link de
 * verificacion. Si se pasan a confirmEmailVerification (solo cuando el verify
 * detecta mismo-navegador), el backend mintea un code y devuelve redirect_to.
 */
export interface VerifyAuthzParams {
  client_id: string;
  redirect_uri: string;
  state: string;
  code_challenge: string;
  code_challenge_method: 'S256';
  scope: string;
  nonce?: string;
}

/**
 * POST /partners/{partnerId}/dbconnections/confirm. Consume el token de
 * verificacion de email (2.12) que llego al usuario por SES.
 *
 * Si `authz` viene, el backend ademas mintea un authorization_code y devuelve
 * `redirect_to` (auto-login → callback del partner). El SPA solo lo pasa cuando
 * detecta mismo-navegador via la cookie `av`.
 */
export async function confirmEmailVerification(
  partnerId: string,
  token: string,
  authz?: VerifyAuthzParams,
): Promise<{
  verified: true;
  user_id: string;
  redirect_to?: string;
  // El usuario tiene un pago pendiente: el SPA debe ofrecer completarlo en vez
  // de dar por cerrado el flujo (no se auto-loguea a la OTT en este caso).
  payment_pending?: boolean;
  /** Solo junto con `redirect_to` (auto-login). Ver lib/browser-identity.ts. */
  browser_ticket?: string;
}> {
  const result = await fetchJson<Awaited<ReturnType<typeof confirmEmailVerification>>>(
    `/partners/${encodeURIComponent(partnerId)}/dbconnections/confirm`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, ...(authz ?? {}) }),
    },
  );
  // Auto-login tras verificar: misma constancia que login().
  saveBrowserTicket(partnerId, result.browser_ticket);
  return result;
}

/**
 * POST /partners/{partnerId}/dbconnections/resend_verification. Reenvia el
 * correo de verificacion. Anti-enumeracion: responde siempre el mismo mensaje.
 * Si se pasan los authz params + verify_nonce (el login portal los tiene), el
 * link reenviado habilita auto-login al confirmar en el mismo navegador.
 */
export async function resendVerification(
  partnerId: string,
  params: { email: string; verify_nonce?: string } & Partial<VerifyAuthzParams>,
): Promise<{ message: string }> {
  return fetchJson(
    `/partners/${encodeURIComponent(partnerId)}/dbconnections/resend_verification`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
  );
}

/**
 * POST /partners/{partnerId}/dbconnections/reset_password. Consume el token
 * de password reset (2.13) y setea la nueva password en Cognito.
 */
export async function completePasswordReset(
  partnerId: string,
  token: string,
  newPassword: string,
): Promise<{ reset: true; user_id: string }> {
  return fetchJson(`/partners/${encodeURIComponent(partnerId)}/dbconnections/reset_password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}

// ---------------------------------------------------------------------------
// Autogestión de la cuenta (/change-password) — la OTT manda al usuario ya
// identificado con ?client_id&id_token_hint[&return_url].
// ---------------------------------------------------------------------------

export interface SelfCareSession {
  access_token: string;
  expires_in: number;
  user_id: string;
  /** true si el return_url recibido está en las redirect_uris del client. */
  return_url_ok: boolean;
}

/**
 * POST /me/session. Canjea el id_token_hint de la OTT por un access_token
 * opaco de TTL corto, usable como Bearer contra /me/*. Sin re-login.
 *
 * Si el enlace es de otra cuenta que la que inició sesión en este navegador,
 * responde `AccountMismatch` (sin access_token) — discriminar con
 * isAccountMismatch().
 */
export async function initSelfCareSession(params: {
  clientId: string;
  idTokenHint: string;
  returnUrl?: string;
  /** Constancia del navegador (lib/browser-identity.ts). */
  browserTicket?: string | null;
}): Promise<SelfCareSession | AccountMismatch> {
  return fetchJson<SelfCareSession | AccountMismatch>('/me/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id: params.clientId,
      id_token_hint: params.idTokenHint,
      ...(params.browserTicket ? { browser_ticket: params.browserTicket } : {}),
      browser_id: getBrowserId(),
      ...(params.returnUrl ? { return_url: params.returnUrl } : {}),
    }),
  });
}

export interface MeAccount {
  user_id: string;
  email: string;
  display_name: string | null;
  status: string;
}

/** GET /me/account (Bearer) — datos de la propia cuenta. */
export async function fetchMeAccount(token: string): Promise<MeAccount> {
  return fetchJson<MeAccount>('/me/account', { headers: authHeaders(token) });
}

/**
 * POST /me/account/change-password (Bearer). Cambia la password del usuario
 * logueado validando la actual.
 *
 * `revokeOtherSessions` lo elige el usuario con un checkbox: cierra sus otras
 * sesiones (TV, celular) sin tocar la actual. Tira ApiError con
 * message === 'current_password_incorrect' si la password actual no valida.
 */
export async function changeMyPassword(
  token: string,
  params: {
    currentPassword: string;
    newPassword: string;
    revokeOtherSessions: boolean;
  },
): Promise<{ changed: true; other_sessions_revoked: number }> {
  return fetchJson('/me/account/change-password', {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      current_password: params.currentPassword,
      new_password: params.newPassword,
      revoke_other_sessions: params.revokeOtherSessions,
    }),
  });
}

/** Info NO sensible de un device_code para la pantalla de aprobacion del QR. */
export interface DeviceCodeInfo {
  status: 'pending' | 'approved' | 'consumed' | 'expired' | 'not_found';
  user_code: string | null;
  device_name: string | null;
  expires_in: number;
}

/**
 * GET /partners/{partnerId}/oauth/device/verify?dc=… — trae el user_code
 * legible (ABCD-EFGH) y el estado para mostrar en la pantalla del QR. El
 * device_code es el secreto (lo escaneó el usuario), así que no requiere auth.
 */
export async function getDeviceCodeInfo(
  partnerId: string,
  deviceCode: string,
): Promise<DeviceCodeInfo> {
  return fetchJson(
    `/partners/${encodeURIComponent(partnerId)}/oauth/device/verify?dc=${encodeURIComponent(deviceCode)}`,
    { method: 'GET' },
  );
}

/**
 * POST /partners/{partnerId}/oauth/device/verify. Autentica al usuario con sus
 * credenciales y aprueba el device_code en un solo paso (no se persiste token
 * en el SPA). `deviceName` es el alias que el usuario le pone (ej "TV Living Casa").
 */
export async function approveDeviceCode(
  partnerId: string,
  params: { deviceCode: string; email: string; password: string; deviceName?: string },
): Promise<{ approved: true } | ConsentRequired> {
  return fetchJson(`/partners/${encodeURIComponent(partnerId)}/oauth/device/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      device_code: params.deviceCode,
      email: params.email,
      password: params.password,
      ...(params.deviceName ? { device_name: params.deviceName } : {}),
    }),
  });
}
